/**
 * 自分のバナーを上げる（広告を作る ② の中・2026-10-22 ssa-founder-1022・founder「AI で作ってもいいし…アップロードでもいい」）。
 *
 * 流れ: 画像を 1 枚選ぶ → ブラウザで 2 つの枠の形（320×50・728×90 の 2 倍）に真ん中を切り抜く（selfAds.ts coverCrop）→ 1 枚ずつ PUT（selfAdsApi.uploadBanner）。
 *   「大きさごとに別の画像」を開けば、スマホ用・PC 用を別々に選べる（もう一方はそのまま）。
 * WHY ブラウザで切り抜くか: サーバーは JPEG を復号しない（依存なしの方針 = apps/server の png.ts）= 決まった形の画像を受けて確かめるだけ。
 *   ついでに描き直すので、写真の中の位置の情報（EXIF）も送らない。
 * WHY JPEG で送るか: 帯は遊ぶ人のページで毎回読まれる = 軽く。透明な所は白で埋める（帯の背景は白ではないが、透明のバナーは枠が見えずに広告と分かりにくい）。
 * WHY createImageBitmap か: <img src="blob:…"> はダッシュボードの CSP（img-src 'self' data:）に入っていない。ファイルを直接読めば CSP を広げずに済む。
 * 中身（エロ・グロ・薬物…）は確認に出すとき AI と運営が見る（ここでは見ない）。
 */
import { useState } from 'react';
import type { ApiError } from '../api';
import { errorText, useT, type MessageKey } from '../i18n';
import { uploadBanner } from '../selfAdsApi';
import { BANNER_SLOTS, SOURCE_MAX_BYTES, UPLOAD_MAX_BYTES, UPLOAD_TYPES, coverCrop, type BannerSize, type Campaign } from '../selfAds';

type Slot = (typeof BANNER_SLOTS)[number];

/** 1 つの枠の形に切り抜いて JPEG にする。重すぎたら質を下げてもう一度（1 MB はほぼ超えないが念のため） */
async function cropTo(bitmap: ImageBitmap, slot: Slot): Promise<Uint8Array> {
  const canvas = document.createElement('canvas');
  canvas.width = slot.outWidth;
  canvas.height = slot.outHeight;
  const g = canvas.getContext('2d');
  if (!g) throw new Error('canvas');
  g.fillStyle = '#ffffff';
  g.fillRect(0, 0, canvas.width, canvas.height);
  g.imageSmoothingQuality = 'high';
  const c = coverCrop(bitmap.width, bitmap.height, slot.outWidth, slot.outHeight);
  g.drawImage(bitmap, c.sx, c.sy, c.sw, c.sh, 0, 0, slot.outWidth, slot.outHeight);
  for (const q of [0.9, 0.75, 0.6]) {
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', q));
    if (blob && blob.size <= UPLOAD_MAX_BYTES) return new Uint8Array(await blob.arrayBuffer());
  }
  throw new Error('too_large');
}

export function BannerUpload({ campaign, onChange, disabled }: { campaign: Campaign; onChange: (c: Campaign) => void; disabled?: boolean }) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  /** 選んだ画像を枠に切り抜いて上げる。sizes = 上げる枠（1 枚で両方 / 片方だけ） */
  const put = async (file: File | undefined, sizes: readonly BannerSize[]) => {
    if (!file) return;
    setErr(null);
    setDone(false);
    // 選ぶ時の案内（中身はサーバーが確かめ直す）。SVG・GIF はここで止める
    if (!(UPLOAD_TYPES as readonly string[]).includes(file.type)) return setErr(t('ssa.banner.uploadNotImage'));
    if (file.size > SOURCE_MAX_BYTES) return setErr(t('ssa.banner.uploadTooBig'));
    setBusy(true);
    try {
      let bitmap: ImageBitmap;
      try {
        bitmap = await createImageBitmap(file);
      } catch {
        setErr(t('ssa.banner.uploadNotImage'));
        return;
      }
      let latest = campaign;
      for (const slot of BANNER_SLOTS.filter((s) => sizes.includes(s.size))) {
        latest = (await uploadBanner(campaign.id, slot.size, await cropTo(bitmap, slot))).campaign;
      }
      bitmap.close();
      onChange(latest);
      setDone(true);
    } catch (e) {
      setErr(e instanceof Error && e.message === 'too_large' ? t('ssa.err.banner_too_large') : errorText(e as ApiError, UPLOAD_ERRORS));
    } finally {
      setBusy(false);
    }
  };

  const picker = (label: string, sizes: readonly BannerSize[], primary = false) => (
    <label className={`btn ${primary ? 'btn-ghost' : 'btn-ghost btn-sm'} ssa-upload-pick ${busy || disabled ? 'is-disabled' : ''}`}>
      {label}
      <input
        type="file"
        className="sr-only"
        accept={UPLOAD_TYPES.join(',')}
        disabled={busy || disabled}
        onChange={(e) => {
          void put(e.target.files?.[0], sizes);
          // 同じ画像をもう一度選んでも動くように空にする
          e.target.value = '';
        }}
      />
    </label>
  );

  return (
    <div className="ssa-upload">
      <p className="small muted">{t('ssa.banner.uploadHelp')}</p>
      <div className="ssa-upload-actions">{picker(busy ? t('ssa.banner.uploading') : t('ssa.banner.uploadPick'), [320, 728], true)}</div>
      <details className="ssa-upload-each">
        <summary className="small">{t('ssa.banner.uploadEach')}</summary>
        <div className="ssa-upload-actions">
          {picker(t('ssa.banner.uploadPhone'), [320])}
          {picker(t('ssa.banner.uploadComputer'), [728])}
        </div>
      </details>
      <div aria-live="polite">
        {done && !err && <p className="small muted">{t('ssa.banner.uploaded')}</p>}
        {err && <p className="alert alert-error">{err}</p>}
      </div>
    </div>
  );
}

/** アップロードの口のエラーの言葉（サーバーの selfAdsUpload.ts の code） */
export const UPLOAD_ERRORS: Partial<Record<string, MessageKey>> = {
  banner_type_invalid: 'ssa.err.banner_type_invalid',
  banner_too_large: 'ssa.err.banner_too_large',
  banner_size_invalid: 'ssa.err.banner_size_invalid',
  banner_animated: 'ssa.err.banner_animated',
  invalid_state: 'ssa.err.invalid_state',
  campaign_not_found: 'ssa.err.campaign_not_found'
};
